import {
  Activity,
  BookOpen,
  CheckCircle2,
  Clock,
  FileText,
  MessageSquare,
  Sprout,
  Tag as TagIcon,
  Trophy,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useStudentTimeline } from '@/hooks/use-timeline';
import type { TimelineEvent, TimelineEventType } from '@/types/timeline';
import { formatRelativeTime } from '@/lib/utils';

const iconFor: Record<TimelineEventType, typeof Sprout> = {
  session_completed: Sprout,
  assignment_created: Clock,
  assignment_completed: CheckCircle2,
  assignment_expired: XCircle,
  report_submitted: FileText,
  report_approved: CheckCircle2,
  report_rejected: XCircle,
  reflection_created: BookOpen,
  note_added: MessageSquare,
  tag_added: TagIcon,
  tag_removed: TagIcon,
  milestone_awarded: Trophy,
  stage_changed: Activity,
};

interface ActivityTimelineProps {
  studentId: string;
}

export function ActivityTimeline({ studentId }: ActivityTimelineProps) {
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useStudentTimeline(studentId);

  const events: TimelineEvent[] =
    data?.pages.flatMap((p) => p.events) ?? [];

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading timeline…</p>;
  }
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">No activity yet.</p>;
  }

  return (
    <div className="space-y-3">
      <ol className="relative border-l pl-6">
        {events.map((event, idx) => {
          const Icon = iconFor[event.type] ?? Activity;
          return (
            <li key={idx} className="mb-4">
              <span className="absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full bg-background ring-2 ring-border">
                <Icon className="h-3 w-3 text-muted-foreground" />
              </span>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium">{event.title}</p>
                <time
                  className="text-xs text-muted-foreground"
                  title={new Date(event.at).toLocaleString()}
                >
                  {formatRelativeTime(event.at)}
                </time>
              </div>
              {event.body && (
                <p className="mt-1 max-w-xl whitespace-pre-wrap text-xs text-muted-foreground">
                  {event.body}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
}
